/**
 * Phase 9.7.6 — 🎁 Live Gift Overlay (polish pass)
 * --------------------------------------------------
 * Listens for `gift` events and renders:
 *   • Floating gift toasts (top-right stack, max 5 visible, TTL 5.5s)
 *   • Combo counter when the same sender sends the same gift within 3s
 *   • A flowing emoji that drifts up from the bottom-right of the
 *     screen for every single gift (cheap or expensive) — gives the
 *     room a constant visible "energy"
 *   • Premium/legendary fullscreen takeover (TTL 4s, longer for the
 *     bigger tiers) with bounce + radial flare + spotlight rays
 *
 * The spotlight glow on a gifted speaker tile is handled by LiveRoomPage
 * directly (it knows the layout of its tile grid).
 */
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";

const TIER_BG = {
  tiny: "bg-gradient-to-r from-pink-300/80 to-rose-400/80",
  small: "bg-gradient-to-r from-pink-500/80 to-rose-500/80",
  medium: "bg-gradient-to-r from-amber-500/85 to-orange-500/85",
  large: "bg-gradient-to-r from-yellow-400 via-amber-500 to-pink-500",
  legendary: "bg-gradient-to-r from-fuchsia-500 via-amber-400 to-cyan-400",
};

const ANIM_LABEL = {
  float: "↑",
  sparkle: "✨",
  confetti: "🎉",
  takeover: "👑",
};

const COMBO_WINDOW_MS = 3000;
const TOAST_TTL_MS = 5500;
const MAX_TOASTS = 5;
const TAKEOVER_MS_BASE = 3000; // large tier
const TAKEOVER_MS_LEGENDARY = 5000; // legendary tier
const FLOW_TTL_MS = 4500;
const MAX_FLOWERS = 14;

const LiveGiftOverlay = forwardRef((_props, ref) => {
  const [toasts, setToasts] = useState([]);
  const [takeover, setTakeover] = useState(null);
  // Phase 9.7.6 — bottom-right flowing emoji stream so every gift makes
  // visible movement, not just stacks in the corner.
  const [flowers, setFlowers] = useState([]);
  // Recent toasts keyed by sender_id + gift_id for combo merging.
  const recentRef = useRef(new Map());
  // Phase 21.4 Batch 4 — Dedupe by transaction_id so a locally-enqueued
  // sender animation isn't re-rendered when the WS broadcast arrives.
  const seenTxIdsRef = useRef(new Map()); // transaction_id → timestamp
  const TX_DEDUPE_WINDOW_MS = 30_000;

  useImperativeHandle(ref, () => ({
    enqueueGift: (msg) => enqueue(msg),
  }), []);

  // Auto-clean flowers
  useEffect(() => {
    if (flowers.length === 0) return;
    const t = setTimeout(() => {
      const cutoff = Date.now() - FLOW_TTL_MS;
      setFlowers((cur) => cur.filter((f) => f.t > cutoff));
    }, 600);
    return () => clearTimeout(t);
  }, [flowers]);

  const enqueue = (msg) => {
    if (!msg?.gift) return;
    // Transaction-id dedupe: skip if we've already animated this exact
    // transaction within the last 30s (covers the sender-enqueue +
    // WS-broadcast race for the same gift).
    const txId = msg.transaction_id;
    if (txId) {
      const now0 = Date.now();
      // Purge stale entries.
      for (const [k, ts] of seenTxIdsRef.current.entries()) {
        if (now0 - ts > TX_DEDUPE_WINDOW_MS) seenTxIdsRef.current.delete(k);
      }
      if (seenTxIdsRef.current.has(txId)) return;
      seenTxIdsRef.current.set(txId, now0);
    }
    const sid = msg.sender?.user_id || "anon";
    const gid = msg.gift.id;
    const key = `${sid}::${gid}`;
    const now = Date.now();
    const prev = recentRef.current.get(key);
    // Phase 21.1b — Power Boost batches arrive with a `quantity` field.
    // Render a single toast with "sent N Roses" instead of N separate toasts.
    const quantity = Math.max(1, Number(msg.quantity || msg.gift?.quantity || 1));

    // Always emit a flower (even on combos) so rapid gifting feels alive.
    // For Power Boosts, drop multiple flowers (capped) so big sends "rain".
    const flowerBurst = Math.min(quantity, 6);
    setFlowers((cur) => {
      const burst = Array.from({ length: flowerBurst }).map((_, i) => ({
        id: `f_${now}_${i}_${Math.random().toString(36).slice(2, 5)}`,
        emoji: msg.gift.emoji,
        size: msg.gift.tier === "legendary" ? 56
              : msg.gift.tier === "large" ? 44
              : msg.gift.tier === "medium" ? 36
              : 28,
        offset: Math.floor(Math.random() * 70) - 30,
        t: now,
      }));
      return [...burst, ...cur].slice(0, MAX_FLOWERS);
    });

    // Combo merge: same sender+gift within window → bump combo counter.
    // Phase 21.3a fix — only merge if the prior toast is STILL on screen.
    // If it was already auto-dismissed but `recentRef` hadn't been
    // cleaned up yet, the previous code silently dropped the new gift
    // (no toast, no takeover, no animation). We now fall through to
    // the normal new-toast path in that case.
    if (prev && now - prev.t < COMBO_WINDOW_MS) {
      let merged = false;
      setToasts((cur) => {
        const idx = cur.findIndex((t) => t.id === prev.id);
        if (idx < 0) return cur;
        merged = true;
        const copy = cur.slice();
        copy[idx] = {
          ...copy[idx],
          combo: (copy[idx].combo || 1) + quantity,
          _refreshedAt: now,
        };
        return copy;
      });
      if (merged) {
        prev.t = now;
        return;
      }
      // Prior toast was already dismissed — fall through to the normal
      // path so we still render a fresh toast + (if applicable) takeover.
    }
    const toastId = `g_${msg.transaction_id || now}_${Math.random().toString(36).slice(2, 6)}`;
    recentRef.current.set(key, { id: toastId, t: now });

    setToasts((cur) => [
      { id: toastId, sender: msg.sender, gift: msg.gift, combo: quantity,
        message: msg.message, recipient: msg.recipient, _refreshedAt: now },
      ...cur,
    ].slice(0, MAX_TOASTS));

    // Tier-large + legendary = fullscreen takeover ONCE (no combo stacking).
    const isTakeover = msg.gift.tier === "large" || msg.gift.tier === "legendary";
    if (isTakeover) {
      const ttl = msg.gift.tier === "legendary" ? TAKEOVER_MS_LEGENDARY : TAKEOVER_MS_BASE;
      setTakeover({
        id: toastId, sender: msg.sender, gift: msg.gift, message: msg.message,
        recipient: msg.recipient, ttl,
      });
      setTimeout(() => {
        setTakeover((cur) => (cur && cur.id === toastId ? null : cur));
      }, ttl);
    }

    // Auto-dismiss toast after TTL of the *last refresh*.
    const dismiss = () => {
      const stored = recentRef.current.get(key);
      if (!stored) return;
      const t = stored.t;
      if (Date.now() - t < TOAST_TTL_MS) {
        setTimeout(dismiss, 250);
        return;
      }
      setToasts((cur) => cur.filter((x) => x.id !== stored.id));
      recentRef.current.delete(key);
    };
    setTimeout(dismiss, TOAST_TTL_MS + 100);
  };

  return (
    <>
      {/* Floating toast stack (top-right) */}
      <div
        className="fixed top-20 right-3 z-[55] flex flex-col gap-2 pointer-events-none"
        data-testid="live-gift-toast-stack"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto px-3 py-2 rounded-xl shadow-lg shadow-black/40 text-white border border-white/20 max-w-xs ${TIER_BG[t.gift.tier] || TIER_BG.small} ${t.combo > 1 ? "scale-105" : ""} transition-transform`}
            style={{
              animation: "gift-toast-pop 360ms cubic-bezier(.4,1.4,.5,1) forwards",
            }}
            data-testid="live-gift-toast"
          >
            <div className="flex items-center gap-2">
              <img
                src={getProfilePhotoUrl({ profile_photo: t.sender?.profile_photo })}
                alt=""
                className="w-7 h-7 rounded-full object-cover border border-white/40"
              />
              <div className="min-w-0 flex-1 text-xs leading-tight">
                <p className="font-bold truncate">{t.sender?.artist_name || "Anon"}</p>
                <p className="text-[11px] text-white/90 truncate">
                  sent{" "}
                  {t.combo > 1 && (
                    <span className="font-black text-white" data-testid="live-gift-combo-inline">
                      {t.combo}×{" "}
                    </span>
                  )}
                  <span className="font-bold">{t.gift.emoji} {t.gift.name}</span>
                  <span className="ml-1 text-[10px] text-white/70">
                    ·{(t.gift.coins * (t.combo > 1 && t.gift.unit_coins ? 1 : 1)).toLocaleString()}c
                  </span>
                </p>
                {t.recipient?.artist_name && (
                  <p className="text-[10px] text-white/75 truncate">
                    → {t.recipient.artist_name}
                  </p>
                )}
              </div>
              {t.combo > 1 && (
                <span
                  className="text-base font-black bg-black/30 px-2 py-0.5 rounded-full ring-1 ring-white/40 animate-pulse"
                  data-testid="live-gift-combo"
                >
                  ×{t.combo}
                </span>
              )}
              <span className="text-xl">{ANIM_LABEL[t.gift.animation] || "↑"}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Phase 9.7.6 — bottom-right flowing emoji stream */}
      <div
        className="fixed bottom-2 right-2 sm:right-6 z-[50] pointer-events-none w-[140px] h-[420px] overflow-hidden"
        data-testid="live-gift-flow"
      >
        {flowers.map((f) => (
          <span
            key={f.id}
            data-testid="live-gift-flower"
            className="absolute bottom-0"
            style={{
              right: `${50 + f.offset}px`,
              fontSize: `${f.size}px`,
              animation: `gift-flow-up ${FLOW_TTL_MS}ms cubic-bezier(.2,.6,.4,1) forwards`,
              filter: "drop-shadow(0 0 10px rgba(245,158,11,0.5))",
            }}
          >
            {f.emoji}
          </span>
        ))}
      </div>

      {/* Fullscreen takeover for large + legendary tier gifts */}
      {takeover && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center pointer-events-none"
          data-testid="live-gift-takeover"
          style={{
            background: takeover.gift.tier === "legendary"
              ? "radial-gradient(circle at 50% 45%, rgba(217,70,239,0.5), rgba(245,158,11,0.3) 35%, rgba(0,0,0,0.92) 70%)"
              : "radial-gradient(circle at 50% 40%, rgba(244,114,182,0.4), rgba(0,0,0,0.88) 65%)",
            animation: `gift-takeover-pulse ${takeover.ttl}ms linear forwards`,
          }}
        >
          {/* Spotlight rays for legendary */}
          {takeover.gift.tier === "legendary" && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(245,158,11,0.18) 25deg, transparent 60deg, rgba(217,70,239,0.18) 110deg, transparent 160deg, rgba(34,211,238,0.15) 220deg, transparent 280deg, rgba(245,158,11,0.18) 330deg, transparent 360deg)",
                animation: "gift-rays-spin 3.5s linear infinite",
              }}
            />
          )}
          <div className="text-center relative">
            <div
              className="text-[120px] md:text-[200px] leading-none mb-4 drop-shadow-[0_0_40px_rgba(255,255,255,0.7)]"
              style={{ animation: "gift-takeover-bounce 800ms cubic-bezier(.3,1.6,.4,1)" }}
            >
              {takeover.gift.emoji}
            </div>
            {takeover.gift.tier === "legendary" && (
              <p className="text-[10px] uppercase tracking-[6px] font-black text-amber-300 mb-2">
                ✨ LEGENDARY ✨
              </p>
            )}
            <div className="text-3xl md:text-5xl font-black text-white mb-1 drop-shadow-lg">
              {takeover.gift.name}
            </div>
            <div className="text-base text-amber-200 font-bold">
              from {takeover.sender?.artist_name || "Anon"}
              {takeover.recipient?.artist_name && (
                <> → <span className="text-pink-200">{takeover.recipient.artist_name}</span></>
              )}
              {" · "}
              <span className="text-white">{takeover.gift.coins.toLocaleString()}c</span>
            </div>
            {takeover.message && (
              <p className="mt-3 text-sm text-white/85 italic max-w-md mx-auto px-4">
                "{takeover.message}"
              </p>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes gift-toast-pop {
          0% { opacity: 0; transform: translateX(60px) scale(0.85); }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes gift-takeover-bounce {
          0% { transform: scale(0.3) rotate(-12deg); opacity: 0; }
          70% { transform: scale(1.15) rotate(4deg); opacity: 1; }
          100% { transform: scale(1) rotate(0); opacity: 1; }
        }
        @keyframes gift-takeover-pulse {
          0% { opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes gift-rays-spin {
          0% { transform: rotate(0deg); opacity: 0.7; }
          100% { transform: rotate(360deg); opacity: 0.7; }
        }
        @keyframes gift-flow-up {
          0%   { transform: translateY(0) rotate(-4deg) scale(0.7); opacity: 0; }
          12%  { opacity: 1; transform: translateY(-30px) rotate(2deg) scale(1); }
          50%  { transform: translateY(-220px) rotate(-3deg) scale(1.05); opacity: 0.95; }
          100% { transform: translateY(-380px) rotate(6deg) scale(0.8); opacity: 0; }
        }
      `}</style>
    </>
  );
});

export default LiveGiftOverlay;
